import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged,
  User as FirebaseUser,
  updateProfile,
  linkWithPopup,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, googleProvider, db, functions } from '../firebase';
import { User } from '../types';

// Get user data function from Firebase
const getUserDataFn = httpsCallable(functions, 'getUserData');

/**
 * Transform Firebase user to app User
 */
export async function transformUser(firebaseUser: FirebaseUser): Promise<User | null> {
  if (!firebaseUser) return null;

  try {
    // Get additional user data from Firestore via Cloud Function
    const result = await getUserDataFn();
    const userData = result.data as {
      uid: string;
      email: string;
      displayName: string | null;
      photoURL: string | null;
      credits: number;
      emailVerified: boolean;
    };

    return {
      id: userData.uid,
      name: userData.displayName || firebaseUser.email?.split('@')[0] || 'Usuário',
      email: userData.email,
      credits: userData.credits,
      photoURL: userData.photoURL || undefined,
      emailVerified: userData.emailVerified,
    };
  } catch (error) {
    console.error('Error getting user data:', error);
    // Return basic user data if cloud function fails
    return {
      id: firebaseUser.uid,
      name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'Usuário',
      email: firebaseUser.email || '',
      credits: 0,
      photoURL: firebaseUser.photoURL || undefined,
      emailVerified: firebaseUser.emailVerified,
    };
  }
}

/**
 * Register new user with email and password
 */
export async function registerWithEmail(
  email: string,
  password: string,
  name: string,
  recaptchaToken: string
): Promise<User> {
  // Validate reCAPTCHA token via Cloud Function (only if token is provided)
  if (recaptchaToken) {
    try {
      const validateRecaptchaFn = httpsCallable(functions, 'validateRecaptcha');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout na validação do reCAPTCHA')), 10000); // 10 seconds
      });
      
      const recaptchaResult = await Promise.race([
        validateRecaptchaFn({ token: recaptchaToken, action: 'register' }),
        timeoutPromise
      ]) as { data?: { success: boolean; error?: string } };
      
      if (!recaptchaResult.data || !recaptchaResult.data.success) {
        const errorMsg = recaptchaResult.data?.error || 'Falha na verificação reCAPTCHA. Por favor, tente novamente.';
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      // If reCAPTCHA validation fails, log but don't block registration in development
      console.error('Erro na validação do reCAPTCHA:', error);
      if (error.message?.includes('Timeout')) {
        throw new Error('A validação do reCAPTCHA está demorando muito. Tente novamente.');
      }
      // In production, we should block, but in dev we can be more lenient
      if (import.meta.env.PROD) {
        throw new Error('Falha na verificação reCAPTCHA. Por favor, tente novamente.');
      } else {
        console.warn('⚠️ Continuando registro sem validação reCAPTCHA (modo desenvolvimento)');
      }
    }
  } else {
    console.warn('⚠️ reCAPTCHA não configurado. Autenticação prosseguindo sem verificação reCAPTCHA.');
  }

  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const firebaseUser = userCredential.user;

  // Update display name
  await updateProfile(firebaseUser, { displayName: name });

  // Create user document in Firestore
  await setDoc(doc(db, 'users', firebaseUser.uid), {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: name,
    photoURL: null,
    credits: 0, // Start with 0 credits
    emailVerified: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Send email verification
  await sendEmailVerification(firebaseUser);

  return {
    id: firebaseUser.uid,
    name: name,
    email: firebaseUser.email || '',
    credits: 0,
    emailVerified: false,
  };
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string,
  recaptchaToken: string
): Promise<User> {
  // Validate reCAPTCHA token via Cloud Function (only if token is provided)
  if (recaptchaToken) {
    try {
      const validateRecaptchaFn = httpsCallable(functions, 'validateRecaptcha');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout na validação do reCAPTCHA')), 10000); // 10 seconds
      });
      
      const recaptchaResult = await Promise.race([
        validateRecaptchaFn({ token: recaptchaToken, action: 'login' }),
        timeoutPromise
      ]) as { data?: { success: boolean; error?: string } };
      
      if (!recaptchaResult.data || !recaptchaResult.data.success) {
        const errorMsg = recaptchaResult.data?.error || 'Falha na verificação reCAPTCHA. Por favor, tente novamente.';
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      // If reCAPTCHA validation fails, log but don't block login in development
      console.error('Erro na validação do reCAPTCHA:', error);
      if (error.message?.includes('Timeout')) {
        throw new Error('A validação do reCAPTCHA está demorando muito. Tente novamente.');
      }
      // In production, we should block, but in dev we can be more lenient
      if (import.meta.env.PROD) {
        throw new Error('Falha na verificação reCAPTCHA. Por favor, tente novamente.');
      } else {
        console.warn('⚠️ Continuando login sem validação reCAPTCHA (modo desenvolvimento)');
      }
    }
  } else {
    console.warn('⚠️ reCAPTCHA não configurado. Autenticação prosseguindo sem verificação reCAPTCHA.');
  }

  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = await transformUser(userCredential.user);
  
  if (!user) {
    throw new Error('Erro ao carregar dados do usuário');
  }
  
  return user;
}

/**
 * Link Google account to existing email/password account
 * User must be logged in with email/password first
 */
export async function linkGoogleAccount(): Promise<User> {
  const currentUser = auth.currentUser;
  
  if (!currentUser) {
    throw new Error('Você precisa estar logado para vincular sua conta do Google.');
  }

  try {
    // Link Google account to current user
    const result = await linkWithPopup(currentUser, googleProvider);
    const firebaseUser = result.user;

    // Update user document with Google info
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      
      // Update with Google photo and display name if available
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        ...userData,
        photoURL: firebaseUser.photoURL || userData.photoURL,
        displayName: firebaseUser.displayName || userData.displayName,
        updatedAt: serverTimestamp(),
      }, { merge: true });
    }

    const user = await transformUser(firebaseUser);
    
    if (!user) {
      throw new Error('Erro ao carregar dados do usuário');
    }
    
    return user;
  } catch (error: any) {
    if (error.code === 'auth/credential-already-in-use') {
      throw new Error('Esta conta do Google já está vinculada a outra conta.');
    }
    throw error;
  }
}

/**
 * Sign in with Google
 * Handles account linking if email already exists with email/password
 */
export async function signInWithGoogle(): Promise<User> {
  try {
    const userCredential = await signInWithPopup(auth, googleProvider);
    const firebaseUser = userCredential.user;

    // Check if user document exists
    const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
    
    if (!userDoc.exists()) {
      // Create new user document
      await setDoc(doc(db, 'users', firebaseUser.uid), {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        credits: 0, // Start with 0 credits
        emailVerified: firebaseUser.emailVerified,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    }

    const user = await transformUser(firebaseUser);
    
    if (!user) {
      throw new Error('Erro ao carregar dados do usuário');
    }
    
    return user;
  } catch (error: any) {
    // Handle account exists with different credential error
    if (error.code === 'auth/account-exists-with-different-credential') {
      const email = error.customData?.email;
      
      if (email) {
        // Check what sign-in methods are available
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);
        
        // If email/password is available, throw a special error to trigger account linking
        if (signInMethods.includes('password')) {
          const linkingError: any = new Error('Este email já está cadastrado com email e senha. Por favor, faça login primeiro e depois vincule sua conta do Google.');
          linkingError.code = 'auth/account-exists-with-different-credential';
          linkingError.email = email;
          throw linkingError;
        }
      }
      
      throw new Error('Este email já está cadastrado com outro método de login.');
    }
    
    throw error;
  }
}

/**
 * Sign out
 */
export async function logout(): Promise<void> {
  await signOut(auth);
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Resend email verification
 */
export async function resendVerificationEmail(): Promise<void> {
  if (auth.currentUser) {
    await sendEmailVerification(auth.currentUser);
  } else {
    throw new Error('Nenhum usuário logado');
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(
  callback: (user: User | null) => void
): () => void {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const user = await transformUser(firebaseUser);
      callback(user);
    } else {
      callback(null);
    }
  });
}

/**
 * Get current Firebase user
 */
export function getCurrentFirebaseUser(): FirebaseUser | null {
  return auth.currentUser;
}

/**
 * Refresh user data from server
 */
export async function refreshUserData(): Promise<User | null> {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) return null;
  
  return transformUser(firebaseUser);
}

